'use client';

import React from 'react';
import type { Locale } from 'date-fns';
import { Box, HStack, VStack, Text } from '@ui-library';

import { CancellationSummaryRow } from '@/models';

import { formatTimelineDate } from './cancellationTimeline';

type CancellationDeadlinesTimelineProps = {
  rows: CancellationSummaryRow[] | null | undefined;
  timeZone: string;
  locale?: Locale;
  dateFormat?: string;
};

/**
 * The BOOK-8062 "Cancellation deadlines" vertical timeline: one milestone per row (bullet + a
 * connecting rail on the left, the resolved date/time in the middle, the action + detail on the
 * right). A row whose dateTime could not be resolved falls back to its relative timingLabel.
 */
export const CancellationDeadlinesTimeline: React.FC<
  CancellationDeadlinesTimelineProps
> = ({ rows, timeZone, locale, dateFormat }) => {
  // Defensive: tolerate a null/undefined rows prop or element slipping through from the API.
  const safeRows = Array.isArray(rows)
    ? rows.filter((row) => Boolean(row))
    : [];

  return (
    <VStack alignItems="stretch" gap={0} w="full">
      {safeRows.map((row, index) => {
        const formatted = formatTimelineDate(
          row.dateTime,
          timeZone,
          locale,
          dateFormat,
        );
        const isLast = index === safeRows.length - 1;

        return (
          <HStack
            key={`${row.anchor ?? 'row'}-${index}`}
            alignItems="stretch"
            gap={3}
            w="full"
          >
            <VStack gap={0} alignItems="center" minW="10px">
              <Box
                boxSize="10px"
                borderRadius="full"
                bg="fog.500"
                mt={1}
                flexShrink={0}
              />
              {!isLast && <Box flex="1" w="1px" bg="fog.300" minH={2} />}
            </VStack>

            <HStack
              alignItems="flex-start"
              gap={3}
              pb={isLast ? 0 : 5}
              w="full"
            >
              <VStack
                gap={0}
                alignItems="flex-start"
                minW="72px"
                maxW="96px"
                flexShrink={0}
              >
                {formatted ? (
                  <>
                    <Text
                      fontWeight="semibold"
                      color="fog.800"
                      overflowWrap="anywhere"
                    >
                      {formatted.date}{' '}
                      <Text as="span" color="fog.600" fontWeight="normal">
                        {formatted.weekday}
                      </Text>
                    </Text>
                    <Text color="fog.600" fontSize="sm">
                      {formatted.time}
                    </Text>
                  </>
                ) : (
                  <Text
                    fontWeight="semibold"
                    color="fog.800"
                    overflowWrap="anywhere"
                  >
                    {row.timingLabel}
                  </Text>
                )}
              </VStack>

              <VStack gap={1} alignItems="flex-start" minW={0} w="full">
                <Text
                  fontWeight="semibold"
                  color="fog.800"
                  overflowWrap="anywhere"
                >
                  {row.actionCopy}
                </Text>
                {row.detail && (
                  <Text color="fog.600" overflowWrap="anywhere">
                    {row.detail}
                  </Text>
                )}
              </VStack>
            </HStack>
          </HStack>
        );
      })}
    </VStack>
  );
};
